import { useState } from 'react';
import { Modal } from '@/components/Modal';
import { useConfirm } from '@/components/ConfirmProvider';
import { Button, FIELD_CLASS, Spinner, cx } from '@/components/ui';
import {
  MAX_WATCHLIST_CATEGORIES,
  WATCHLIST_CATEGORY_NAME_MAX,
  type WatchlistCategory,
  type WatchlistItem,
} from './stockTypes';
import { useWatchlistCategoryMutations } from './useStocks';

/**
 * 관심종목 카테고리 관리. (3.10)
 *
 * 만들기 · 이름 바꾸기 · 지우기가 한 화면에 모인다.
 * 카테고리가 몇 개 안 되어 목록·상세를 나누지 않고 한 번에 보여준다(처음엔 최대 3개였다 — 개수 제한은 풀었다).
 *
 * 🔴 지우기는 무엇이 사라지고 무엇이 남는지 확인창에 적는다
 *   "카테고리를 삭제할까요?" 만으로는 안에 담긴 종목이 함께 지워지는지 알 수 없다.
 *   실제로는 관심종목에 그대로 남는데, 그것을 모르면 지우기를 누르지 못한다.
 */

interface Props {
  open: boolean;
  onClose: () => void;
  categories: WatchlistCategory[];
  /** 카테고리별 건수를 세기 위해 받는다. 서버가 세어 주지 않는다 */
  items: WatchlistItem[];
}

export function WatchlistCategoryModal({ open, onClose, categories, items }: Props) {
  const confirm = useConfirm();
  const { create, rename, remove } = useWatchlistCategoryMutations();

  const [newName, setNewName] = useState('');

  // 어느 줄을 고치는 중인지. null 이면 아무것도 고치고 있지 않다.
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');

  /*
    상한에 닿았나. (2026-09-10)

    🔴 MAX_WATCHLIST_CATEGORIES 가 0 이면 제한이 없다는 뜻이다
      상한을 없애면서 상수를 지우지 않고 0 으로 뒀다. 그래야 나중에
      다시 걸 때 이 한 줄만 되돌리면 된다.
  */
  const full =
    MAX_WATCHLIST_CATEGORIES > 0 && categories.length >= MAX_WATCHLIST_CATEGORIES;
  const busy = create.isPending || rename.isPending || remove.isPending;

  // 세 뮤테이션의 오류를 한 자리에 모아 보여준다.
  // 각 줄에 따로 달면 화면이 오류 문구로 얼룩진다.
  const errorMessage =
    create.error?.message ?? rename.error?.message ?? remove.error?.message;

  /** 한 종목이 여러 카테고리에 들어갈 수 있어 소속 목록을 뒤진다 */
  function itemCount(categoryId: number): number {
    return items.filter((item) =>
      item.categories.some((ref) => ref.categoryId === categoryId),
    ).length;
  }

  function 만들기() {
    const name = newName.trim();
    if (name === '') {
      return;
    }
    create.mutate(name, {
      onSuccess: () => setNewName(''),
    });
  }

  function 이름저장(categoryId: number) {
    const name = editingName.trim();
    if (name === '') {
      return;
    }
    rename.mutate({ categoryId, name }, { onSuccess: () => setEditingId(null) });
  }

  async function 지우기(category: WatchlistCategory) {
    const count = itemCount(category.id);

    const ok = await confirm({
      title: `'${category.name}' 카테고리를 지울까요?`,
      description:
        count === 0
          ? '담긴 종목이 없습니다.'
          : `담긴 ${count}개 종목은 관심종목에 그대로 남습니다. 이 분류만 사라집니다.`,
      confirmText: '지우기',
      tone: 'danger',
    });

    if (ok) {
      remove.mutate(category.id);
    }
  }

  return (
    <Modal open={open} onClose={onClose} maxWidth="sm:max-w-md" labelledBy="cat-title">
      <div className="p-4">
        <div className="mb-1 flex items-center justify-between">
          <h2 id="cat-title" className="text-base font-semibold text-fg">
            카테고리 관리
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="닫기"
            className="rounded p-1 text-fg-subtle hover:bg-surface-hover hover:text-fg-muted"
          >
            ✕
          </button>
        </div>

        <p className="mb-4 text-xs text-fg-subtle">
          관심종목을 나눠 담는 묶음입니다. 개수 제한은 없습니다 - 탭이 많아지면
          옆으로 밀어 볼 수 있습니다.
        </p>

        {/* ── 목록 ── */}
        {categories.length === 0 ? (
          <p className="mb-4 rounded-lg bg-surface-muted px-3 py-4 text-center text-sm text-fg-muted">
            아직 카테고리가 없습니다. 아래에서 만들어 보세요.
          </p>
        ) : (
          <ul className="mb-4 flex flex-col divide-y divide-line">
            {categories.map((category) => {
              const count = itemCount(category.id);
              const editing = editingId === category.id;

              return (
                <li key={category.id} className="flex items-center gap-2 py-2">
                  {editing ? (
                    <>
                      <input
                        value={editingName}
                        onChange={(event) => setEditingName(event.target.value)}
                        maxLength={WATCHLIST_CATEGORY_NAME_MAX}
                        autoFocus
                        className={cx(FIELD_CLASS, 'flex-1')}
                      />
                      <Button
                        variant="primary"
                        onClick={() => 이름저장(category.id)}
                        disabled={busy || editingName.trim() === ''}
                      >
                        저장
                      </Button>
                      <Button variant="ghost" onClick={() => setEditingId(null)}>
                        취소
                      </Button>
                    </>
                  ) : (
                    <>
                      <span className="min-w-0 flex-1 truncate text-sm text-fg">
                        {category.name}
                      </span>
                      <span className="shrink-0 text-xs text-fg-subtle">{count}개</span>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setEditingId(category.id);
                          setEditingName(category.name);
                        }}
                      >
                        이름 바꾸기
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => void 지우기(category)}
                        disabled={busy}
                      >
                        지우기
                      </Button>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {/* ── 만들기 ── */}
        <div className="flex items-center gap-2">
          <input
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
            onKeyDown={(event) => {
              // 폼이 아니라 입력칸 하나뿐이라 Enter 를 직접 잡는다.
              if (event.key === 'Enter') {
                만들기();
              }
            }}
            maxLength={WATCHLIST_CATEGORY_NAME_MAX}
            placeholder={full ? `${MAX_WATCHLIST_CATEGORIES}개를 모두 썼습니다` : '예: 단기 · 배당'}
            disabled={full}
            className={cx(FIELD_CLASS, 'flex-1')}
          />
          <Button
            variant="primary"
            onClick={만들기}
            disabled={full || busy || newName.trim() === ''}
          >
            만들기
          </Button>
        </div>

        {errorMessage && <p className="mt-3 text-sm text-danger">{errorMessage}</p>}

        {busy && (
          <div className="mt-3">
            <Spinner label="저장하는 중" />
          </div>
        )}
      </div>
    </Modal>
  );
}
